import { Box, Field as ChakraField, defineStyle } from "@chakra-ui/react";
import * as React from "react";
import { Alert } from "../ui/alert";
import { InfoTip } from "../ui/toggle-tip";

export interface FieldProps extends Omit<ChakraField.RootProps, "label"> {
  label: React.ReactNode;
  isShowLabel?: boolean;
  disableLabel?: boolean;
  helperText?: React.ReactNode;
  errorText?: string | null;
  optionalText?: React.ReactNode;
  labelProps?: { [key: string]: any };
  labelContainerFlexDirection?: "row" | "column";
}

export const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  function Field(props, ref) {
    const {
      label,
      isShowLabel = true,
      disableLabel = false,
      children,
      helperText,
      errorText,
      optionalText,
      disabled,
      labelProps,
      labelContainerFlexDirection = "row",
      ...rest
    } = props;

    const helperTextValue = helperText
      ? helperText
      : `Please provide ${label} field`;
    const errorTextValue = errorText
      ? errorText
      : label
      ? `Please fill ${label}`
      : `Please fill the field`;

    return (
      <ChakraField.Root
        disabled={disableLabel}
        ref={ref}
        {...(labelContainerFlexDirection === "row"
          ? (fieldContentStyle as any)
          : (fieldContentColumnStyle as any))}
        textStyle="fieldContent"
        {...rest}
      >
        {label && isShowLabel && (
          <ChakraField.Label
            {...(fieldLabelStyle as any)}
            textStyle="fieldLabel"
            {...labelProps}
          >
            {label}
            <ChakraField.HelperText color={"fg.info"}>
              <InfoTip>
                <Alert>{helperTextValue}</Alert>
              </InfoTip>
            </ChakraField.HelperText>
            <ChakraField.RequiredIndicator fallback={optionalText} />
          </ChakraField.Label>
        )}
        <Box flex={1}>
          {children}

          <Box flex={1} minH={"1rem"}>
            {!disabled && (
              <ChakraField.ErrorText color={"fg.error"}>
                <Alert fontSize="0.75rem" status="error" whiteSpace="pre-wrap">
                  {errorTextValue}
                </Alert>
              </ChakraField.ErrorText>
            )}
          </Box>
        </Box>
      </ChakraField.Root>
    );
  }
);

export const fieldContentStyle = defineStyle({
  flex: 1,
  flexDirection: "row",
  gap: { base: 2, lg: 5 },
  minWidth: { base: "100%", md: "300px" },
  width: "100%",
  height: "100%",
  alignItems: "center",
  justifyContent: "flex-start",
  pointerEvents: "auto",
  textStyle: "fieldContent",
});

export const fieldContentColumnStyle = defineStyle({
  flex: 1,
  flexDirection: "column",
  gap: 0,
  minWidth: { base: "100%", md: "300px" },
  width: "100%",
  height: "100%",
  alignItems: "flex-start",
  justifyContent: "center",
  pointerEvents: "auto",
  textStyle: "fieldContent",
});
export const fieldLabelStyle = defineStyle({
  whiteSpace: "nowrap",
  minWidth: { base: "150px", md: "200px", lg: "250px" },
  width: "fit-content",
  alignItems: "center",
  fontWeight: "bold",
  mb: 0,
  textStyle: "fieldLabel",
  color: "tertiary.fg",
});
